/**
 * International phone input built on react-phone-number-input.
 * Inspired by https://github.com/omeralpi/shadcn-phone-input
 * Uses project UI (Popover) for country select and matches Input styling.
 */
/* eslint-disable react-refresh/only-export-components -- re-exports for validation/utilities */

import * as React from 'react';
import PhoneInputWithCountry from 'react-phone-number-input/max';
import type { Value } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui';
import { cn } from '@/lib/cn';
import {
  getCountryCallingCode,
  type Country,
} from 'react-phone-number-input/max';

export interface PhoneInputProps extends Omit<
  React.ComponentProps<typeof PhoneInputWithCountry>,
  'onChange' | 'value' | 'inputComponent' | 'countrySelectComponent'
> {
  label?: React.ReactNode;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  defaultCountry?: Country;
}

/**
 * Custom input that matches our Input styling (no border on left, rounded right).
 */
const PhoneInputField = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  return (
    <input
      ref={ref}
      type='tel'
      autoComplete='tel'
      className={cn(
        'flex min-h-10 min-w-0 flex-1 rounded-r-md border border-l-0 border-slate-200 bg-white px-3 text-sm outline-none',
        className,
      )}
      {...props}
    />
  );
});

type CountrySelectProps = {
  value: Country | undefined;
  options: Array<{ value: Country | undefined; label: string }>;
  onChange: (country: Country | undefined) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  iconComponent: React.ComponentType<{
    country: Country;
    label: string;
  }>;
};

function CountrySelect({
  value: selectedCountry,
  options,
  onChange,
  onFocus,
  onBlur,
  disabled,
  iconComponent: IconComponent,
}: CountrySelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const filtered = React.useMemo(() => {
    if (!search.trim()) {
      return options;
    }
    const q = search.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.value !== null &&
          o.value !== undefined &&
          getCountryCallingCode(o.value).includes(q)),
    );
  }, [options, search]);

  const selectedOption = options.find((o) => o.value === selectedCountry);
  const selectedLabel =
    selectedOption?.label ?? selectedCountry ?? 'International';

  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <PopoverTrigger wrapperClassName='flex-shrink-0'>
        <button
          type='button'
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          className='flex min-h-10 items-center gap-1 rounded-l-md border-r border-slate-200 bg-slate-50 px-3 py-2 focus:z-10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:opacity-50'
        >
          {selectedCountry !== null && selectedCountry !== undefined ? (
            <IconComponent country={selectedCountry} label={selectedLabel} />
          ) : (
            <span className='text-xs text-slate-500' aria-hidden>
              🌐
            </span>
          )}
          <span className='text-slate-500' aria-hidden>
            ▼
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className='flex max-h-72 w-[300px] flex-col p-0'>
        <input
          type='text'
          placeholder='Search country...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className='border-b border-slate-200 px-3 py-2 text-sm outline-none'
        />
        <div className='min-h-0 flex-1 overflow-y-auto'>
          {filtered
            .filter(
              (o): o is { value: Country; label: string } =>
                o.value !== null && o.value !== undefined,
            )
            .map(({ value: country, label: countryName }) => (
              <button
                key={country}
                type='button'
                className='flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-100'
                onClick={() => {
                  onChange(country);
                  setOpen(false);
                }}
              >
                <IconComponent country={country} label={countryName} />
                <span className='flex-1'>{countryName}</span>
                <span className='text-slate-500'>
                  +{getCountryCallingCode(country)}
                </span>
              </button>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function PhoneInput({
  label,
  value,
  onChange,
  placeholder = '(000) 000-0000',
  defaultCountry = 'US',
  className,
  disabled,
  ...rest
}: PhoneInputProps) {
  const e164Value = value?.trim() ? (value as Value) : undefined;
  const handleChange = (v: Value | undefined) => {
    onChange?.(v ? String(v) : '');
  };

  return (
    <label className={cn('flex w-full flex-col gap-1 text-sm', className)}>
      {label ? (
        <span className='text-xs font-medium text-slate-600'>{label}</span>
      ) : null}
      <PhoneInputWithCountry
        defaultCountry={defaultCountry}
        value={e164Value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        smartCaret={false}
        numberInputProps={{ className: 'PhoneInputInput' }}
        inputComponent={PhoneInputField}
        countrySelectComponent={CountrySelect as never}
        className={cn(
          'flex min-h-10 items-center overflow-hidden rounded-md border border-slate-200 bg-white',
        )}
        {...rest}
      />
    </label>
  );
}

// Re-exports for validation/utilities (react-refresh expects only components above).
export {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'react-phone-number-input/max';
export type { Country, Value } from 'react-phone-number-input';
